import React from 'react';
import { calculateAge, generateFirstNameInitial, generateLastNameInitial, groupItemsByType, calculatePeriod, formatGender } from '../../../../utils/webCvUtils';
import dayjs from 'dayjs';
import sty from './WebCvPDF.module.css';
import PropTypes from 'prop-types';

const WebCvPDF = ({ data }) => {
    // prop
    WebCvPDF.propTypes = {
        data: PropTypes.object.isRequired,
    };
    
    const engineer = data?.engineer || {};
    const profile = data?.profile || {};
    const qualifications = data?.qualifications || [];
    const projects = data?.projects || [];
        // プロジェクトを日付でソートする
    projects.sort((a, b) => {
        // 年の比較
        const yearA = parseInt(a.start_year);
        const yearB = parseInt(b.start_year);
        
        if (yearB !== yearA) {
            return yearB - yearA; // 年の降順
        }
        
        // 月の比較
        const monthA = parseInt(a.start_month);
        const monthB = parseInt(b.start_month);
        return monthB - monthA; // 月の降順
    });

    // 現在の日付をフォーマット
    const currentDate = dayjs().format('YYYY年MM月DD日');

    return (
        <>
            <div className={sty["a4-page"]}>
                <div className={sty['skill-sheet']}>
                    {/* タイトル行 */}
                    <div className={sty['title-cell']}>スキルシート</div>
                    
                    {/* 作成日部分 */}
                    <div className={sty['date-cell']}>作成日：{currentDate}</div>
                    
                    {/* 氏名行 */}
                    <div className={`${sty['header-cell']} ${sty['name-label']}`}>氏名</div>
                    <div className={sty['lastname-value']}>{generateLastNameInitial(engineer.last_name_kana)}</div>
                    <div className={sty['firstname-value']}>{generateFirstNameInitial(engineer.first_name_kana)}</div>
                    <div className={`${sty['header-cell']} ${sty['gender-label']}`}>性別</div>
                    <div className={`${sty['header-cell']} ${sty['age-label']}`}>年齢</div>
                    <div className={`${sty['header-cell']} ${sty['station-label']}`}>最寄り駅</div>
                    <div className={sty['gender-value']}>{formatGender(engineer.gender)}</div>
                    <div className={sty['age-value']}>{calculateAge(engineer.birth_year, engineer.birth_month, engineer.birth_day)}</div>
                    <div className={sty['station-value']}>
                        {engineer.closest_station ? `${engineer.closest_station}駅` : ''}
                        {engineer.closest_station2 ? `/${engineer.closest_station2}駅` : ''}
                    </div>
                    
                    {/* 最終学歴行 */}
                    <div className={`${sty['header-cell']} ${sty['education-label']}`}>最終学歴</div>
                    <div className={sty['education-value']}>{engineer.school_name}{engineer.school_subject}</div>
                    <div className={`${sty['header-cell']} ${sty['qualification-header-cell']}`}>保有資格</div>
                    <div className={sty['qualification-name-header']}>資格名</div>
                    <div className={sty['qualification-date-header']}>取得年月</div>
                    
                    {/* 得意分野と資格行 */}
                    <div 
                        className={`${sty['header-cell']} ${sty['specialty-label']}`}
                        style={{ gridRow: `span ${Math.max(qualifications.length, 3)}` }}
                    >
                        得意分野
                    </div>
                    <div 
                        className={`${sty['specialty-value']} ${sty['skill-list']}`}
                        style={{ gridRow: `span ${Math.max(qualifications.length, 3)}` }}
                    >
                        {profile?.input?.speciality || ''}
                    </div>

                    {Array.from({ length: Math.max(qualifications.length, 3) }, (_, index) => {
                        const qualification = qualifications[index];
                        return (
                            <React.Fragment key={`qualification-${index}`}>
                                <div className={`${sty['qualification-name']} ${sty['qualification-name' + (index + 1)]}`}>
                                    {qualification?.qualification_name || ''}
                                </div>
                                <div className={`${sty['qualification-date']} ${sty['qualification-date' + (index + 1)]}`}>
                                    {qualification ? `${qualification.qualification_year}年${qualification.qualification_month}月` : ''}
                                </div>
                            </React.Fragment>
                        );
                    })}
                    
                    {/* 経歴概要セクション */}
                    <div className={sty['section-header']} data-page-break="true">経歴概要</div>
                    <div className={sty['career-overview']}>
                        {profile?.input?.career_overview || ''}
                    </div>
                    
                    {/* 今後の方向性セクション */}
                    <div className={sty['section-header']} data-page-break="true">今後の方向性</div>
                    <div className={sty['future-direction']}>
                        {profile?.input?.future_direction || ''}
                    </div>
                </div>
            
                {/* 業務経歴書ページ */}
                {projects.length > 0  && (
                    <div className={sty['work-title']} data-page-break="true">業務経歴</div>
                )}
                {projects.map((project, index) => (
                    <React.Fragment key={`project-${index}`}>
                        <div className={sty['work-history']} data-page-break={index === 0 ? "false" :"true"}>
                            
                            {/* No.行 */}
                            <div className={sty['work-header-no']}>No.</div>
                            <div className={sty['work-header-number']}>{index + 1}</div>
                            <div className={sty['work-header-role']}>業種/役割/規模</div>
                            <div className={sty['work-header-project']}>プロジェクト名 / 業務概要</div>
                            
                            {/* 期間行 */}
                            <div className={sty['work-period-label']}>期間</div>
                            <div className={sty['work-client-type']}>
                                <div>{project.industry}</div>
                            </div>
                            <div className={sty['work-project-name']}>
                                {project.project_name}
                            </div>
                            
                            {/* 年月日行 */}
                            <div className={sty['work-date']}>
                                <div>{project.start_year}/{project.start_month}</div>
                                <div className={sty['date-separator']}>~</div>
                                <div>{(!project.end_year || project.end_year === '') && 
                                    (!project.end_month || project.end_month === '') 
                                    ? '現在' 
                                    : `${project.end_year}/${project.end_month}`}
                                </div>
                            </div>
                            <div className={sty['work-role']}>{project.role}</div>
                            <div className={sty['work-description']}>
                                {project.overview}
                            </div>
                            
                            {/* 計算期間行 */}
                            <div className={sty['work-period']}>
                                {calculatePeriod(project.start_year, project.start_month, project.end_year, project.end_month)}
                            </div>
                            <div className={sty['work-members']}>{project.scale}</div>
                        </div>

                        <div className={sty['phase']} data-page-break="true">
                            <div className={sty['header-phase']}>担当工程</div>

                            {/* 担当工程タイトル行 */}
                            <div className={sty['header-basic']}>要件定義</div>
                            <div className={sty['header-detail']}>基本設計</div>
                            <div className={sty['header-functional']}>詳細設計</div>
                            <div className={sty['header-implementation']}>実装・構築</div>
                            <div className={sty['header-unit']}>単体テスト</div>
                            <div className={sty['header-integration']}>結合テスト</div>
                            <div className={sty['header-system']}>総合テスト</div>
                            <div className={sty['header-operation']}>負荷テスト</div>
                            <div className={sty['header-security']}>セキュリティ<br/>テスト</div>
                            <div className={sty['header-maintenance']}>保守・運用</div>
                            
                            <div className={sty['data-basic']}>
                                {project.process.requirement_analysis ? '■' : '□'}
                            </div>
                            <div className={sty['data-detail']}>
                                {project.process.basic_design ? '■' : '□'}
                            </div>
                            <div className={sty['data-functional']}>
                                {project.process.detailed_design ? '■' : '□'}
                            </div>
                            <div className={sty['data-implementation']}>
                                {project.process.coding ? '■' : '□'}
                            </div>
                            <div className={sty['data-unit']}>
                                {project.process.unit_test ? '■' : '□'}
                            </div>
                            <div className={sty['data-integration']}>
                                {project.process.integration_test ? '■' : '□'}
                            </div>
                            <div className={sty['data-system']}>
                                {project.process.system_test ? '■' : '□'}
                            </div>
                            <div className={sty['data-operation']}>
                                {project.process.load_test ? '■' : '□'}
                            </div>
                            <div className={sty['data-security']}>
                                {project.process.security_test ? '■' : '□'}
                            </div>
                            <div className={sty['data-maintenance']}>
                                {project.process.operation ? '■' : '□'}
                            </div>
                        </div>

                        {/* システム/作業環境セクション*/}
                        {(() => {
                            // 6つの環境項目がすべて空かどうかチェック
                            const serverItems = groupItemsByType(project.server, 'server_type', 'server_con');
                            const clientItems = groupItemsByType(project.client, 'client_type', 'client_con');
                            const devItems = groupItemsByType(project.dev, 'dev_type', 'dev_con');
                            const cloudItems = groupItemsByType(project.cloud, 'cloud_type', 'cloud_con');
                            const pcItems = groupItemsByType(project.pc, 'pc_type', 'pc_con');
                            const networkItems = groupItemsByType(project.network, 'network_type', 'network_con');
                            
                            // すべての項目が空の場合は何も表示しない
                            const hasEnvironmentItems = 
                                Object.keys(serverItems).length > 0 || 
                                Object.keys(clientItems).length > 0 || 
                                Object.keys(devItems).length > 0 || 
                                Object.keys(cloudItems).length > 0 || 
                                Object.keys(pcItems).length > 0 || 
                                Object.keys(networkItems).length > 0;
                            
                            // 項目がない場合は何も表示しない
                            if (!hasEnvironmentItems) {
                                return null;
                            }
                            
                            // 項目がある場合はセクションを表示
                            return (
                                <div className={sty['env-section']} data-page-break="true">
                                    <div className={sty['env-header']}>システム/作業環境</div>
                                    
                                    {/* サーバーとクライアント */}
                                    {(Object.keys(groupItemsByType(project.server, 'server_type', 'server_con')).length > 0 || 
                                    Object.keys(groupItemsByType(project.client, 'client_type', 'client_con')).length > 0) && (
                                        <>
                                            <div className={sty['env-category-left']}>サーバー</div>
                                            <div className={sty['env-category-right']}>クライアント</div>
                                            
                                            {(() => {
                                                const maxRows = Math.max(
                                                    Object.keys(serverItems).length,
                                                    Object.keys(clientItems).length
                                                );
                                                
                                                return [...Array(maxRows)].map((_, rowIndex) => {
                                                    const serverKeys = Object.keys(serverItems);
                                                    const clientKeys = Object.keys(clientItems);
                                                    
                                                    return (
                                                        <React.Fragment key={`server-client-row-${rowIndex}`}>
                                                            {/* サーバー列 */}
                                                            {rowIndex < serverKeys.length ? (
                                                                <>
                                                                    <div className={`${sty['env-type-label']} ${rowIndex < maxRows - 1 ? sty['env-item-not-last'] : ''}`}>
                                                                        {serverKeys[rowIndex]}
                                                                    </div>
                                                                    <div className={`${sty['env-type-value']} ${rowIndex < maxRows - 1 ? sty['env-item-not-last'] : ''}`}>
                                                                        {serverItems[serverKeys[rowIndex]].join(', ')}
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <div className={`${sty['env-type-label-empty']} ${rowIndex < maxRows - 1 ? sty['env-item-not-last'] : ''}`}></div>
                                                                    <div className={`${sty['env-type-value-empty']} ${rowIndex < maxRows - 1 ? sty['env-item-not-last'] : ''}`}></div>
                                                                </>
                                                            )}
                                                            
                                                            {/* クライアント列 */}
                                                            {rowIndex < clientKeys.length ? (
                                                                <>
                                                                    <div className={`${sty['env-type-label']} ${rowIndex < maxRows - 1 ? sty['env-item-not-last'] : ''}`}>
                                                                        {clientKeys[rowIndex]}
                                                                    </div>
                                                                    <div className={`${sty['env-type-value']} ${rowIndex < maxRows - 1 ? sty['env-item-not-last'] : ''}`}>
                                                                        {clientItems[clientKeys[rowIndex]].join(', ')}
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <div className={`${sty['env-type-label-empty']} ${rowIndex < maxRows - 1 ? sty['env-item-not-last'] : ''}`}></div>
                                                                    <div className={`${sty['env-type-value-empty']} ${rowIndex < maxRows - 1 ? sty['env-item-not-last'] : ''}`}></div>
                                                                </>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                });
                                            })()}
                                        </>
                                    )}

                                    {/* 開発環境とクラウド */}
                                    {(Object.keys(groupItemsByType(project.dev, 'dev_type', 'dev_con')).length > 0 || 
                                    Object.keys(groupItemsByType(project.cloud, 'cloud_type', 'cloud_con')).length > 0) && (
                                        <>
                                            <div className={sty['env-category-left']}>開発</div>
                                            <div className={sty['env-category-right']}>クラウド</div>
                                            
                                            {(() => {
                                                const maxRows = Math.max(
                                                    Object.keys(devItems).length,
                                                    Object.keys(cloudItems).length
                                                );
                                                
                                                return [...Array(maxRows)].map((_, rowIndex) => {
                                                    const devKeys = Object.keys(devItems);
                                                    const cloudKeys = Object.keys(cloudItems);
                                                    
                                                    return (
                                                        <React.Fragment key={`dev-cloud-row-${rowIndex}`}>
                                                            {/* 開発列 */}
                                                            {rowIndex < devKeys.length ? (
                                                                <>
                                                                    <div className={`${sty['env-type-label']} ${rowIndex < maxRows - 1 ? sty['env-item-not-last'] : ''}`}>
                                                                        {devKeys[rowIndex]}
                                                                    </div>
                                                                    <div className={`${sty['env-type-value']} ${rowIndex < maxRows - 1 ? sty['env-item-not-last'] : ''}`}>
                                                                        {devItems[devKeys[rowIndex]].join(', ')}
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <div className={`${sty['env-type-label-empty']} ${rowIndex < maxRows - 1 ? sty['env-item-not-last'] : ''}`}></div>
                                                                    <div className={`${sty['env-type-value-empty']} ${rowIndex < maxRows - 1 ? sty['env-item-not-last'] : ''}`}></div>
                                                                </>
                                                            )}
                                                            
                                                            {/* クラウド列 */}
                                                            {rowIndex < cloudKeys.length ? (
                                                                <>
                                                                    <div className={`${sty['env-type-label']} ${rowIndex < maxRows - 1 ? sty['env-item-not-last'] : ''}`}>
                                                                        {cloudKeys[rowIndex]}
                                                                    </div>
                                                                    <div className={`${sty['env-type-value']} ${rowIndex < maxRows - 1 ? sty['env-item-not-last'] : ''}`}>
                                                                        {cloudItems[cloudKeys[rowIndex]].join(', ')}
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <div className={`${sty['env-type-label-empty']} ${rowIndex < maxRows - 1 ? sty['env-item-not-last'] : ''}`}></div>
                                                                    <div className={`${sty['env-type-value-empty']} ${rowIndex < maxRows - 1 ? sty['env-item-not-last'] : ''}`}></div>
                                                                </>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                });
                                            })()}
                                        </>
                                    )}
                                    
                                    {/* 作業PCとネットワーク */}
                                    {(Object.keys(groupItemsByType(project.pc, 'pc_type', 'pc_con')).length > 0 || 
                                    Object.keys(groupItemsByType(project.network, 'network_type', 'network_con')).length > 0) && (
                                        <>
                                            <div className={sty['env-category-left']}>作業PC</div>
                                            <div className={sty['env-category-right']}>ネットワーク</div>
                                            
                                            {(() => {
                                                const maxRows = Math.max(
                                                    Object.keys(pcItems).length,
                                                    Object.keys(networkItems).length
                                                );
                                                
                                                return [...Array(maxRows)].map((_, rowIndex) => {
                                                    const pcKeys = Object.keys(pcItems);
                                                    const networkKeys = Object.keys(networkItems);
                                                    
                                                    return (
                                                        <React.Fragment key={`pc-network-row-${rowIndex}`}>
                                                            {/* PC列 */}
                                                            {rowIndex < pcKeys.length ? (
                                                                <>
                                                                    <div className={`${sty['env-type-label']} ${rowIndex < maxRows - 1 ? sty['env-item-not-last'] : ''}`}>
                                                                        {pcKeys[rowIndex]}
                                                                    </div>
                                                                    <div className={`${sty['env-type-value']} ${rowIndex < maxRows - 1 ? sty['env-item-not-last'] : ''}`}>
                                                                        {pcItems[pcKeys[rowIndex]].join(', ')}
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <div className={`${sty['env-type-label-empty']} ${rowIndex < maxRows - 1 ? sty['env-item-not-last'] : ''}`}></div>
                                                                    <div className={`${sty['env-type-value-empty']} ${rowIndex < maxRows - 1 ? sty['env-item-not-last'] : ''}`}></div>
                                                                </>
                                                            )}
                                                            
                                                            {/* ネットワーク列 */}
                                                            {rowIndex < networkKeys.length ? (
                                                                <>
                                                                    <div className={`${sty['env-type-label']} ${rowIndex < maxRows - 1 ? sty['env-item-not-last'] : ''}`}>
                                                                        {networkKeys[rowIndex]}
                                                                    </div>
                                                                    <div className={`${sty['env-type-value']} ${rowIndex < maxRows - 1 ? sty['env-item-not-last'] : ''}`}>
                                                                        {networkItems[networkKeys[rowIndex]].join(', ')}
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <div className={`${sty['env-type-label-empty']} ${rowIndex < maxRows - 1 ? sty['env-item-not-last'] : ''}`}></div>
                                                                    <div className={`${sty['env-type-value-empty']} ${rowIndex < maxRows - 1 ? sty['env-item-not-last'] : ''}`}></div>
                                                                </>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                });
                                            })()}
                                        </>
                                    )}
                                </div>
                            );
                        })()}
                    </React.Fragment>
                ))}
            </div>
        </>
    );
};

export default WebCvPDF;
